import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiAcceptFriendRequest,
  apiCancelFriendRequest,
  apiDeclineFriendRequest,
  apiListFriends,
  apiListIncomingFriendRequests,
  apiListOutgoingFriendRequests,
  apiRemoveFriend,
  apiSendFriendRequest,
  type FriendListItem,
  type FriendRequest,
} from '@/lib/api';
import { qk } from '@/lib/queryKeys';

export function useFriends() {
  return useQuery({
    queryKey: qk.friends(),
    queryFn: () => apiListFriends().then((r) => r.items),
  });
}

export function useIncomingFriendRequests() {
  return useQuery({
    queryKey: qk.friendsIncoming(),
    queryFn: () => apiListIncomingFriendRequests().then((r) => r.items),
  });
}

export function useOutgoingFriendRequests() {
  return useQuery({
    queryKey: qk.friendsOutgoing(),
    queryFn: () => apiListOutgoingFriendRequests().then((r) => r.items),
  });
}

export function useSendFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiSendFriendRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['friends'] });
    },
  });
}

export function useAcceptFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiAcceptFriendRequest,
    onMutate: async (userId: string) => {
      await qc.cancelQueries({ queryKey: qk.friendsIncoming() });
      const prev = qc.getQueryData<FriendRequest[]>(qk.friendsIncoming());
      if (prev) {
        qc.setQueryData<FriendRequest[]>(
          qk.friendsIncoming(),
          prev.filter((r) => r.user.id !== userId),
        );
      }
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.friendsIncoming(), ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['friends'] });
    },
  });
}

export function useDeclineFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiDeclineFriendRequest,
    onMutate: async (userId: string) => {
      await qc.cancelQueries({ queryKey: qk.friendsIncoming() });
      const prev = qc.getQueryData<FriendRequest[]>(qk.friendsIncoming());
      if (prev) {
        qc.setQueryData<FriendRequest[]>(
          qk.friendsIncoming(),
          prev.filter((r) => r.user.id !== userId),
        );
      }
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.friendsIncoming(), ctx.prev);
    },
  });
}

export function useCancelFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiCancelFriendRequest,
    onMutate: async (userId: string) => {
      await qc.cancelQueries({ queryKey: qk.friendsOutgoing() });
      const prev = qc.getQueryData<FriendRequest[]>(qk.friendsOutgoing());
      if (prev) {
        qc.setQueryData<FriendRequest[]>(
          qk.friendsOutgoing(),
          prev.filter((r) => r.user.id !== userId),
        );
      }
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.friendsOutgoing(), ctx.prev);
    },
  });
}

export function useRemoveFriend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: apiRemoveFriend,
    onMutate: async (userId: string) => {
      await qc.cancelQueries({ queryKey: qk.friends() });
      const prev = qc.getQueryData<FriendListItem[]>(qk.friends());
      if (prev) {
        qc.setQueryData<FriendListItem[]>(
          qk.friends(),
          prev.filter((f) => f.user.id !== userId),
        );
      }
      return { prev };
    },
    onError: (_e, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.friends(), ctx.prev);
    },
  });
}
